import {
  isPostingMetric,
  isScreenerMetric,
  isSentimentMetric,
  type MetricValues,
} from '../../PageConfigProvider/types/metric';

export type MetricGroup =
  | 'TD'
  | 'TRANSITION'
  | 'SCREENER'
  | 'SCREENER_COMPATIBLE'
  | 'OTHER';

export const getMetricGroup = (value: string): MetricGroup => {
  if (value === 'td_users') return 'TD';
  if (value === 'transitions_inflow' || value === 'transitions_outflow') {
    return 'TRANSITION';
  }
  if (isScreenerMetric(value as MetricValues) || value === 'market_share') {
    return 'SCREENER';
  }
  if (
    isSentimentMetric(value as MetricValues) ||
    isPostingMetric(value as MetricValues)
  ) {
    return 'SCREENER_COMPATIBLE';
  }
  return 'OTHER';
};

const isScreenerCompatible = (value: string): boolean => {
  const metric = value as MetricValues;
  return (
    isScreenerMetric(metric) ||
    isSentimentMetric(metric) ||
    isPostingMetric(metric) ||
    value === 'market_share'
  );
};

export const deriveDominantGroup = (
  selected: ReadonlyArray<string>
): MetricGroup | undefined => {
  if (!selected || selected.length === 0) return undefined;
  const lastGroup = getMetricGroup(selected?.[selected.length - 1] ?? '');
  if (lastGroup === 'SCREENER_COMPATIBLE') return 'SCREENER';
  return lastGroup;
};

export const deriveSelectedWithDisabled = (
  allOptions: ReadonlyArray<{ value: string; label: string }>,
  selected: ReadonlyArray<string>
): Array<{ value: string; label: string; isDisabled?: boolean }> => {
  const dominant = deriveDominantGroup(selected);

  return selected
    .map((val) => allOptions.find((o) => o.value === val))
    .filter((o): o is { value: string; label: string } => Boolean(o))
    .map((o) => {
      if (!dominant) return { ...o, isDisabled: false };

      if (dominant === 'SCREENER') {
        return { ...o, isDisabled: !isScreenerCompatible(o.value) };
      }

      const isDisabled = getMetricGroup(o.value) !== dominant;
      return { ...o, isDisabled };
    });
};

export const filterEffectiveMetrics = (
  selected: ReadonlyArray<string>
): MetricValues[] => {
  const dominant = deriveDominantGroup(selected);
  if (!dominant) return selected as MetricValues[];

  if (dominant === 'TD') {
    return ['td_users'] as MetricValues[];
  }

  if (dominant === 'TRANSITION') {
    return selected.filter(
      (v) => getMetricGroup(v) === 'TRANSITION'
    ) as MetricValues[];
  }

  if (dominant === 'SCREENER') {
    return selected.filter((v) => isScreenerCompatible(v)) as MetricValues[];
  }

  return selected.filter(
    (v) => getMetricGroup(v) === 'OTHER'
  ) as MetricValues[];
};

export const getIsolationHint = (
  selectedWithDisabled: ReadonlyArray<{ isDisabled?: boolean; value: string }>
): string | null => {
  const hasDisabled = selectedWithDisabled.some((s) => s.isDisabled);
  if (!hasDisabled) return null;

  const dominantGroup = deriveDominantGroup(
    selectedWithDisabled.map((s) => s.value)
  );

  if (dominantGroup === 'TD') {
    return 'Talent Discovery variables cannot be combined with other variables.';
  }
  if (dominantGroup === 'TRANSITION') {
    return 'Only Transitions Inflow and Outflow can be combined; other variables are disabled.';
  }
  if (dominantGroup === 'SCREENER') {
    return 'Screener variables can only be combined with other Screener, Sentiment and Posting variables.';
  }
  return 'This variable cannot be combined with Transitions, Talent Discovery, or Screener variables.';
};
